import { apiClient } from './api';
import { ApiResponse } from '@/types';
import { User } from '@/types/user';
import { ProfileApiResponse } from './profileApi';
import { CapsuleListResponse, capsulesApi } from './capsulesApi';

export interface UserSearchResult {
  id: string | number;
  handle?: string;
  username?: string;
  name?: string;
  email?: string;
  avatar?: string | null;
  is_followed?: boolean;
}

export function mapSearchResultToUser(result: UserSearchResult): User {
  const [firstName = '', lastName = ''] = result.name
    ? result.name.split(' ')
    : ['', ''];
  const normalizedHandle =
    (result.handle || result.username || '').trim() ||
    (result.name || '').trim();

  return {
    id: String(result.id),
    handle: normalizedHandle,
    avatar: result.avatar || undefined,
    is_followed: result.is_followed,
    personal: {
      firstName,
      lastName,
      middleName: '',
      bio: '',
      dateOfBirth: undefined,
      gender: 'not_selected',
    },
    contact: {
      email: result.email || '',
      phone: undefined,
      socials: [],
    },
    location: {
      label: '',
      birthPlace: {},
      residencePlace: {},
    },
    metrics: {
      followers: 0,
      following: 0,
      coauthors: 0,
    },
    preferences: {
      language: 'ru',
      avatarVisibility: 'everyone',
    },
    security: {
      password: '',
      hasSubscription: false,
      lastLogin: undefined,
    },
    createdAt: undefined,
    updatedAt: undefined,
  };
}

export interface UserSearchParams {
  q?: string;
  limit?: number;
  offset?: number;
  mutual_only?: boolean;
}

export interface UserSearchResponse {
  users: UserSearchResult[];
  total?: number;
  limit?: number;
  offset?: number;
  hasMore?: boolean;
}

export const usersApi = {
  async searchUsers(
    params?: UserSearchParams
  ): Promise<ApiResponse<UserSearchResponse>> {
    const queryParams = new URLSearchParams();

    if (params?.q) {
      queryParams.append('query', params.q);
    }
    if (params?.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }
    if (params?.mutual_only) {
      queryParams.append('mutual_only', '1');
    }

    const queryString = queryParams.toString();
    const endpoint = `/users/search/${queryString ? `?${queryString}` : ''}`;
    return apiClient.get<UserSearchResponse>(endpoint);
  },

  async getUserInfo(
    userId: string | number
  ): Promise<ApiResponse<ProfileApiResponse>> {
    return apiClient.get<ProfileApiResponse>(`/users/${userId}/info/`);
  },

  async getUserCapsules(
    userId: string | number
  ): Promise<ApiResponse<CapsuleListResponse>> {
    return capsulesApi.getCapsules({ authorId: String(userId) });
  },

  async followUser(
    userId: string | number
  ): Promise<ApiResponse<{ message?: string }>> {
    return apiClient.post<{ message?: string }>(`/users/${userId}/follow/`, {});
  },

  async unfollowUser(userId: string | number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/users/${userId}/follow/`);
  },
};
